import { useMutation } from '@apollo/client/react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, message } from 'antd';
import clsx from 'clsx';
import { sortBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../AppContext';
import {
  AddButton,
  DeleteButton,
  DragIcon,
  EditButton,
  Import,
} from '../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../common/CommonUseLazyQuery';
import {
  BREAKPOINTS,
  BULK_ACTION,
  BULK_JOB_STATUS,
  CSV_SAMPLE_FILES,
  DEFAULT_PAGE_SIZE,
  DESELECT_MESSAGE,
  GA_EVENT,
  GA_LABEL,
  REQUEST_FEATURE_UPGRADE_KEYS,
} from '../../../../../common/constants';
import { Event } from '../../../../../common/trackEvents';
import useRouter from '../../../../../common/useRouter';
import CommonBulkDeleteModal from '../../../../../components/CommonBulkDeleteModal';
import CommonImportModal from '../../../../../components/CommonImportModal';
import CommonSelectionBar from '../../../../../components/CommonSelectionBar';
import CommonTable from '../../../../../components/CommonTable';
import CommonTooltip from '../../../../../components/CommonTooltip';
import CustomCheckboxSelection from '../../../../../components/CustomCheckboxSelection';
import '../../../../../components/CustomCheckboxSelection.less';
import EllipsisText from '../../../../../components/EllipsisText';
import Portal from '../../../../../components/Portal';
import UpgradeModal from '../../../../../components/UpgradeModal';
import {
  BULK_DELETE_EQC_TYPE_STAGES,
  DELETE_STAGE,
  IMPORT_EQC_TYPE_CSV,
  REORDER_STAGE,
} from '../../../graphql/Mutation';
import {
  GET_BULK_EQC_STAGES_JOB_STATUS,
  GET_STAGE_LIST,
} from '../../../graphql/Queries';
import DeleteModalStage from '../DeleteModalStage';
import AddEditStageModal from './AddEditStageModal';
import BulkEditStageListDrawer from './BulkEditStageListDrawer';

const RowContext = React.createContext({});

const DragHandle = ({ disabled }) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <DragIcon
      className={clsx('cursor-move', disabled && 'cursor-not-allowed')}
      ref={setActivatorNodeRef}
      {...(!disabled && listeners)}
    />
  );
};

const Rows = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    // eslint-disable-next-line react/destructuring-assignment
    id: props['data-row-key'],
  });
  const style = {
    // eslint-disable-next-line react/destructuring-assignment
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners],
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const StageListTable = ({
  setStageId,
  stageId,
  setModalVisible,
  setModalTitle,
  setStageData,
  setRefetchStageData,
  setIsStageSelectionActive,
  isStageItemSelectionActive,
}) => {
  const { getCurrentUser, getTenantUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const {
    params: { eqcTypeId },
  } = useRouter();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const currentTenant = getTenantUser();

  const sampleFileUrl = CSV_SAMPLE_FILES.STAGE_CSV;
  const list = [
    'Stage Type',
    'Checklist Point',
    'Input',
    'Description(Text Only)',
  ];
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const initialStageFilter = {
    skip: 0,
    limit: 10,
    eqcTypeId: Number(eqcTypeId),
    sortBy: { field: 'order', order: 'ASC' },
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [showModal, setShowModal] = useState(false);
  const [deleteStageModal, setDeleteStageModal] = useState(false);
  const [localStageData, setLocalStageData] = useState();
  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [mutationId, setMutationId] = useState('');
  const [dataSource, setDataSource] = useState([]);
  const [stageFilter, setStageFilter] = useState(initialStageFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllAcrossPages, setIsSelectAllAcrossPages] = useState(false);
  const [exceptIds, setExceptIds] = useState([]);
  const [showBulkEditDrawer, setShowBulkEditDrawer] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const bulkJobsRef = useRef(new Map());
  const stageFilterRef = useRef(stageFilter);
  stageFilterRef.current = stageFilter;

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const isAnyRowSelected =
    (selectedRows?.length ?? 0) > 0 || isSelectAllAcrossPages;

  const [fetchStageData, { loading }] = useLazyQueryWithCallbacks(
    GET_STAGE_LIST,
    {
      variables: { filter: stageFilter },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const stageList = res?.eqcTypeStageList?.data || [];
        const matchedStage = stageId
          ? stageList?.find((stage) => stage?.id === stageId)
          : null;
        const activeStage = matchedStage || stageList?.[0];
        setStageId(activeStage?.id);
        setStageData?.(activeStage);

        const pagination = {
          ...paginationProp,
          total: res?.eqcTypeStageList?.total,
        };
        setPaginationProp(pagination);
        setDataSource(sortBy(stageList, ['order']));

        // Handle cross-page selection for newly loaded pages
        if (isSelectAllAcrossPages) {
          const existingIds = selectedRows?.map((row) => row?.id) || [];
          const newItemsToAdd =
            stageList?.filter(
              (item) =>
                !existingIds?.includes(item?.id) &&
                !exceptIds?.includes(item?.id),
            ) || [];
          const updatedSelectedRows = [...selectedRows, ...newItemsToAdd];
          const updatedSelectedKeys =
            updatedSelectedRows
              ?.map((row) => row?.id)
              ?.filter((id) => !exceptIds?.includes(id)) || [];
          setSelectedRows(updatedSelectedRows);
          setSelectedRowKeys(updatedSelectedKeys);
        }
      },
      onError() {},
    },
  );
  const handleRefetchAfterDelete = (deletedCount = 1) => {
    const currentTotal = paginationProp?.total || 0;
    const pageSize = paginationProp?.pageSize || DEFAULT_PAGE_SIZE;
    const newTotal = Math.max(0, currentTotal - deletedCount);
    const maxPage = Math.max(1, Math.ceil(newTotal / pageSize));
    const newCurrent = Math.min(paginationProp?.current || 1, maxPage);
    const newSkip = (newCurrent - 1) * pageSize;

    setStageFilter({
      ...stageFilter,
      skip: newSkip,
    });
    setPaginationProp({
      ...paginationProp,
      current: newCurrent,
    });
    fetchStageData({
      variables: {
        filter: {
          ...stageFilter,
          skip: newSkip,
        },
      },
    });
  };
  const [deleteStage] = useMutation(DELETE_STAGE, {
    onError() {},
    onCompleted() {
      Event(GA_EVENT.DELETE_GLOBAL_CHECKLIST_STAGE, {
        label: GA_LABEL.DELETE_GLOBAL_CHECKLIST_STAGE,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        checklist_id: eqcTypeId,
        stage_id: mutationId,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setDeleteStageModal(false);
      handleRefetchAfterDelete();
    },
  });

  const [updateStageImport, { loading: importLoading }] = useMutation(
    IMPORT_EQC_TYPE_CSV,
    {
      onError() {},
    },
  );

  const startJobTracking = useCallback((jobId, action) => {
    if (!jobId || !action) return;
    bulkJobsRef.current?.set(jobId, { action, timeoutId: null });
  }, []);

  const updateJobTimeout = useCallback((jobId, timeoutId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo) {
      bulkJobsRef.current?.set(jobId, { ...jobInfo, timeoutId });
    }
  }, []);

  const clearJobTracking = useCallback((jobId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo?.timeoutId) {
      clearTimeout(jobInfo?.timeoutId);
    }
    bulkJobsRef.current?.delete(jobId);
  }, []);

  const getJobAction = useCallback((jobId) => {
    if (!jobId) return null;
    return bulkJobsRef.current?.get(jobId)?.action || null;
  }, []);

  const refetchStageDetails = () => {
    const newTotal = (paginationProp?.total || 0) + 1;
    const pageSize = paginationProp?.pageSize || DEFAULT_PAGE_SIZE;
    const lastPage = Math.ceil(newTotal / pageSize);
    const lastPageSkip = (lastPage - 1) * pageSize;

    setStageFilter((prev) => ({ ...prev, skip: lastPageSkip }));
    setPaginationProp((prev) => ({
      ...prev,
      total: newTotal,
      current: lastPage,
    }));
    fetchStageData({
      variables: { filter: { ...stageFilter, skip: lastPageSkip } },
    });
  };

  const handleClearBulkSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setIsSelectAllAcrossPages(false);
    setExceptIds([]);
    setBulkAction(null);
  }, []);

  const [getBulkEqcStageJobStatus] = useLazyQueryWithCallbacks(
    GET_BULK_EQC_STAGES_JOB_STATUS,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        const { state, jobId } = res?.getBulkStageJobStatus;

        if (!jobId) return;

        const currentAction = getJobAction(jobId);

        if (state === BULK_JOB_STATUS.PROCESSING) {
          const timeoutId = setTimeout(() => {
            getBulkEqcStageJobStatus({
              variables: { jobId },
            });
          }, 3000);
          updateJobTimeout(jobId, timeoutId);
        }

        if (
          state === BULK_JOB_STATUS.COMPLETED ||
          state === BULK_JOB_STATUS.FAILED
        ) {
          clearJobTracking(jobId);
          if (state === BULK_JOB_STATUS.COMPLETED) {
            if (currentAction === BULK_ACTION.DELETE) {
              handleRefetchAfterDelete(selectedRows?.length);
            } else {
              fetchStageData({ variables: { filter: stageFilter } });
            }
          }
          handleClearBulkSelection();
        }
      },
    },
  );

  const [bulkDeleteStages, { loading: bulkDeleteLoading }] = useMutation(
    BULK_DELETE_EQC_TYPE_STAGES,
    {
      onCompleted(response) {
        const { status, jobId } = response?.bulkDeleteEqcTypeStages;

        if (status === 'SUCCESS' && jobId) {
          startJobTracking(jobId, BULK_ACTION.DELETE);
          getBulkEqcStageJobStatus({
            variables: { jobId },
          });
        }
        setShowBulkDeleteModal(false);
      },
      onError() {},
    },
  );

  useEffect(() => {
    fetchStageData({ variables: { filter: stageFilter } });
    // Set the refetch function for the parent to use (maintains current pagination)
    setRefetchStageData?.(
      () => () =>
        fetchStageData({ variables: { filter: stageFilterRef.current } }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore selections when data changes
  useEffect(() => {
    if (selectedRows?.length > 0 && dataSource?.length > 0) {
      const currentPageIds = dataSource?.map((item) => item?.id) || [];
      const selectedIdsOnCurrentPage =
        selectedRows
          ?.filter(
            (row) =>
              currentPageIds?.includes(row?.id) &&
              !(isSelectAllAcrossPages && exceptIds?.includes(row?.id)),
          )
          ?.map((row) => row?.id) || [];
      setSelectedRowKeys(selectedIdsOnCurrentPage);
    } else if (selectedRows?.length === 0 && dataSource?.length > 0) {
      setSelectedRowKeys([]);
    }
  }, [dataSource, selectedRows]);

  // Auto-clear selection when all items have been excluded
  useEffect(() => {
    if (
      isSelectAllAcrossPages &&
      exceptIds?.length >= (paginationProp?.total || 0) &&
      paginationProp?.total > 0
    ) {
      handleClearBulkSelection();
    }
  }, [
    exceptIds,
    isSelectAllAcrossPages,
    paginationProp?.total,
    handleClearBulkSelection,
  ]);

  useEffect(() => {
    return () => {
      bulkJobsRef?.current?.forEach((jobInfo) => {
        if (jobInfo?.timeoutId) {
          clearTimeout(jobInfo?.timeoutId);
        }
      });
      bulkJobsRef.current?.clear();
    };
  }, []);

  useEffect(() => {
    setIsStageSelectionActive?.(isAnyRowSelected);
  }, [isAnyRowSelected, setIsStageSelectionActive]);

  const handleRowSelectionChange = (selectedKeys, selectedRowsData) => {
    if (isSelectAllAcrossPages) {
      const currentPageKeys = dataSource?.map((item) => item?.id) || [];
      const newlyDeselected =
        currentPageKeys?.filter((key) => !selectedKeys?.includes(key)) || [];
      const reSelected =
        selectedKeys?.filter((key) => exceptIds?.includes(key)) || [];
      setExceptIds((prev) => {
        let updated = [...prev];
        newlyDeselected?.forEach((id) => {
          if (!updated?.includes(id)) updated.push(id);
        });
        updated = updated?.filter((id) => !reSelected?.includes(id));
        return updated;
      });
      setSelectedRowKeys(selectedKeys);
      return;
    }
    const currentPageKeys = dataSource?.map((item) => item?.id) || [];
    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !currentPageKeys?.includes(row?.id)) || [];
    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...selectedRowsData,
    ];
    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];
    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectRow = (record, selected) => {
    const recordId = record?.id;

    if (selected) {
      if (isSelectAllAcrossPages) {
        // Re-selecting a previously excluded item
        setExceptIds((prev) => prev?.filter((id) => id !== recordId) || []);
        setSelectedRowKeys((prev) => [...prev, recordId]);
        return;
      }
      const isAlreadySelected = selectedRows?.some(
        (row) => row?.id === recordId,
      );
      const newSelectedRows = isAlreadySelected
        ? selectedRows
        : [...selectedRows, record];
      const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];
      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedKeys);
    } else {
      if (isSelectAllAcrossPages) {
        // Keep cross-page mode ON; track excluded ID
        setExceptIds((prev) => [...prev, recordId]);
        const remainingKeys =
          selectedRowKeys?.filter((key) => key !== recordId) || [];
        setSelectedRowKeys(remainingKeys);
        return;
      }
      const newSelectedRows = selectedRows?.filter(
        (row) => row?.id !== recordId,
      );
      const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];
      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedKeys);
    }
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      if (isSelectAllAcrossPages) {
        const currentPageKeys = dataSource?.map((item) => item?.id) || [];
        setExceptIds(
          (prev) => prev?.filter((id) => !currentPageKeys?.includes(id)) || [],
        );
        setSelectedRowKeys(currentPageKeys);
        return;
      }
      const allKeys = dataSource?.map((item) => item?.id) || [];
      setSelectedRowKeys(allKeys);
      setSelectedRows(dataSource || []);
    } else {
      if (isSelectAllAcrossPages) {
        const currentPageKeys = dataSource?.map((item) => item?.id) || [];
        setExceptIds((prev) => {
          const updated = [...prev];
          currentPageKeys?.forEach((id) => {
            if (!updated?.includes(id)) updated.push(id);
          });
          return updated;
        });
        setSelectedRowKeys([]);
        return;
      }
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  };

  const handleSelectAllOnPage = () => {
    const currentPageKeys = dataSource?.map((item) => item?.id) || [];
    setIsSelectAllAcrossPages(false);
    setExceptIds([]);
    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !currentPageKeys?.includes(row?.id)) || [];
    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...(dataSource || []),
    ];
    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];
    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectAllAcrossPages = async () => {
    try {
      setExceptIds([]);
      setIsSelectAllAcrossPages(true);
      const currentPageKeys = dataSource?.map((item) => item?.id) || [];
      setSelectedRowKeys(currentPageKeys);
      setSelectedRows(dataSource || []);
    } catch (error) {
      // Handle error silently
    }
  };

  const [reorderStage, { loading: reorderLoading }] = useMutation(
    REORDER_STAGE,
    {
      onCompleted() {
        Event(GA_EVENT.REORDER_GLOBAL_CHECKLIST_STAGE, {
          label: GA_LABEL.REORDER_GLOBAL_CHECKLIST_STAGE,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          checklist_id: eqcTypeId,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
      },
      onError() {},
    },
  );

  const handleAddEditUser = (record) => {
    if (record) {
      setLocalStageData(record);
    } else {
      setLocalStageData();
    }
    setShowModal(true);
  };

  const handleBulkDelete = () => {
    if (isAnyRowSelected) {
      setShowBulkDeleteModal(true);
      setBulkAction(BULK_ACTION.DELETE);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (isAnyRowSelected) {
      try {
        const mutationData = {
          isAllSelected: isSelectAllAcrossPages,
        };

        if (!isSelectAllAcrossPages) {
          mutationData.eqcTypeStageIds =
            selectedRows?.map((row) => row?.id) || [];
        }
        if (isSelectAllAcrossPages) {
          mutationData.eqcTypeId = Number(eqcTypeId);
        }
        if (isSelectAllAcrossPages && exceptIds?.length > 0) {
          mutationData.exceptEqcTypeStageIds = exceptIds;
        }

        await bulkDeleteStages({
          variables: {
            data: mutationData,
          },
        });
      } catch (error) {
        message.error('Failed to delete stages');
      }
    }
  };

  const handleTableChange = (pagination) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setStageFilter({ ...stageFilter, skip, limit: pagination.pageSize });
    setPaginationProp({ ...paginationProp, ...pagination });

    // Clear current page visual selections but keep selectedRows intact
    if (!isSelectAllAcrossPages) {
      setSelectedRowKeys([]);
    }

    fetchStageData({
      variables: {
        filter: {
          ...stageFilter,
          skip,
          limit: pagination.pageSize,
        },
      },
    });
  };
  const handleImport = () => {
    setShowImportModal(true);
  };
  const handleEdit = (e, record) => {
    e?.stopPropagation?.();
    handleAddEditUser(record);
  };
  const handleDelete = (e, record) => {
    e?.stopPropagation?.();
    setTitle('Stage');
    setName(record?.name);
    setMutationId(record?.id);
    setDeleteStageModal(true);
  };

  // Row selection configuration
  const getRowSelection = () => {
    if (!isDesktopViewport) {
      return undefined;
    }

    return {
      selectedRowKeys,
      onChange: handleRowSelectionChange,
      onSelect: handleSelectRow,
      onSelectAll: handleSelectAll,
      getCheckboxProps: (record) => ({
        disabled: false,
        name: record?.name,
      }),
      columnTitle: (
        <CustomCheckboxSelection
          checked={
            selectedRowKeys?.length === dataSource?.length &&
            dataSource?.length > 0
          }
          indeterminate={
            selectedRowKeys?.length > 0 &&
            selectedRowKeys?.length < dataSource?.length
          }
          disabled={!dataSource?.length}
          onSelectAllPage={handleSelectAllOnPage}
          onSelectAllAcross={handleSelectAllAcrossPages}
          onDeselectAll={handleClearBulkSelection}
          currentPageCount={dataSource?.length}
          totalCount={paginationProp?.total}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          selectedCount={
            isSelectAllAcrossPages
              ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
              : selectedRowKeys?.length || 0
          }
        />
      ),
    };
  };

  const columns = [
    {
      width: '20%',
      align: 'center',
      className: 'drag-visible',
      render: () => (
        <CommonTooltip
          getPopupContainer={() =>
            // eslint-disable-next-line no-undef
            document.querySelector('.ant-table-content')
          }
          title="Change order"
        >
          <div>
            <DragHandle disabled={isAnyRowSelected} />
          </div>
        </CommonTooltip>
      ),
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      className: 'drag-visible',
      key: 'name',
      width: '70%',
      render: (record) => (
        <div>
          <EllipsisText text={record} />
        </div>
      ),
    },
    {
      key: 'action',
      render: (record, _, index) => {
        const isDisabled = isAnyRowSelected || isStageItemSelectionActive;
        const getTooltipTitle = (action) => {
          if (isStageItemSelectionActive) {
            return 'Please de-select stage items first';
          }
          if (isAnyRowSelected) {
            return DESELECT_MESSAGE;
          }
          return action === 'edit' ? 'Edit' : 'Delete';
        };
        return (
          <div
            id={`stage-action-icons-${index}`}
            className="d-flex action-icons"
          >
            <CommonTooltip
              id={`stage-edit-tooltip-${index}`}
              trigger="hover"
              title={getTooltipTitle('edit')}
            >
              <Button
                id={`stage-edit-btn-${index}`}
                shape="circle"
                icon={<EditButton />}
                className={clsx(
                  'edit-button pointer b-0',
                  isDisabled && 'disabled-button',
                )}
                onClick={(e) => handleEdit(e, record)}
                disabled={isDisabled}
              />
            </CommonTooltip>
            <CommonTooltip
              id={`stage-delete-tooltip-${index}`}
              trigger="hover"
              title={getTooltipTitle('delete')}
            >
              <Button
                id={`stage-delete-btn-${index}`}
                className={clsx(
                  'delete-button pointer b-0',
                  isDisabled && 'disabled-button',
                )}
                shape="circle"
                icon={<DeleteButton />}
                onClick={(e) => handleDelete(e, record)}
                disabled={isDisabled}
              />
            </CommonTooltip>
          </div>
        );
      },
    },
  ];

  const DraggableBodyRow = async ({ over, active }) => {
    if (!active || !over || active?.id === over?.id) return;
    if (isAnyRowSelected) return;
    const overIndex = over?.data?.current?.sortable?.index;
    const activeIndex = active?.data?.current?.sortable?.index;
    const data = [...dataSource];
    setDataSource(arrayMove(data, activeIndex, overIndex));
    try {
      const { errors } = await reorderStage({
        variables: {
          data: {
            eqcTypeStageIds: [active?.id, over?.id],
            eqcTypeId: Number(eqcTypeId),
          },
        },
      });
      if (errors) {
        throw errors;
      }
    } catch (error) {
      setDataSource(data);
    }
  };

  const getAddButtonTooltip = () => {
    if (isStageItemSelectionActive || isAnyRowSelected) return DESELECT_MESSAGE;
    return '';
  };
  const addButtonTooltip = getAddButtonTooltip();

  return (
    <div id="stage-list-container">
      {/* Selection Bar */}
      {isDesktopViewport && isAnyRowSelected && (
        <Portal portalId="stage-selection-bar">
          <CommonSelectionBar
            selectedCount={
              isSelectAllAcrossPages
                ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
                : selectedRows?.length || 0
            }
            onClear={handleClearBulkSelection}
          >
            <CommonTooltip trigger="hover" title="Bulk Edit">
              <Button
                shape="round"
                icon={<EditButton />}
                className="edit-button pointer b-0"
                onClick={() => {
                  setShowBulkEditDrawer(true);
                  setBulkAction(BULK_ACTION.EDIT);
                }}
              />
            </CommonTooltip>
            <CommonTooltip trigger="hover" title="Bulk Delete">
              <Button
                className="delete-button pointer b-0 ml-5 mr-5"
                shape="round"
                icon={<DeleteButton />}
                onClick={handleBulkDelete}
              />
            </CommonTooltip>
          </CommonSelectionBar>
        </Portal>
      )}

      {showModal && (
        <AddEditStageModal
          showModal={showModal}
          setShowModal={setShowModal}
          stageData={localStageData}
          isUpdate={!!localStageData}
          setStageData={setLocalStageData}
          refetchStageDataWithInitialValues={refetchStageDetails}
          eqcTypeId={eqcTypeId}
        />
      )}
      {deleteStageModal && (
        <DeleteModalStage
          showModal={deleteStageModal}
          setShowModal={setDeleteStageModal}
          title={title}
          name={name}
          deleteStage={deleteStage}
          mutationId={mutationId}
          setMutationId={setMutationId}
        />
      )}
      {showImportModal && (
        <CommonImportModal
          showImportModal={showImportModal}
          setShowImportModal={setShowImportModal}
          title="Checklist Stages"
          sampleFileUrl={sampleFileUrl}
          list={list}
          updateStageImport={updateStageImport}
          refetchData={fetchStageData}
          filePrefix="eqcTypeCsv"
          eqcTypeId={eqcTypeId}
          stageUi
          importLoading={importLoading}
        />
      )}
      {showBulkDeleteModal && (
        <CommonBulkDeleteModal
          showModal={showBulkDeleteModal}
          setShowModal={setShowBulkDeleteModal}
          selectedCount={
            isSelectAllAcrossPages
              ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
              : selectedRows?.length || 0
          }
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          totalCount={paginationProp?.total || 0}
          onConfirm={handleBulkDeleteConfirm}
          loading={bulkDeleteLoading}
          entityName="stage"
          entityNamePlural="stages"
        />
      )}

      {/* Bulk Edit Drawer */}
      {showBulkEditDrawer && (
        <BulkEditStageListDrawer
          showDrawer={showBulkEditDrawer}
          setShowDrawer={setShowBulkEditDrawer}
          selectedRowKeys={selectedRowKeys}
          selectedRows={selectedRows}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          exceptIds={exceptIds}
          paginationProp={paginationProp}
          bulkAction={bulkAction}
          getBulkEqcStageJobStatus={getBulkEqcStageJobStatus}
          startJobTracking={startJobTracking}
          onSuccess={() => {
            handleClearBulkSelection();
          }}
          eqcTypeId={eqcTypeId}
        />
      )}

      <Portal portalId="eqc-details-btn">
        <Portal portalId="add-btn">
          <CommonTooltip title={addButtonTooltip}>
            <Button
              shape="round"
              type="primary"
              id="add-btn"
              icon={<AddButton />}
              className="add-button b-0"
              disabled={isStageItemSelectionActive || isAnyRowSelected}
              onClick={() => {
                if (
                  paginationProp?.total <
                  currentTenant?.tenant?.featureConfig?.stageMaxLimit
                ) {
                  handleAddEditUser();
                } else {
                  setShowUpgradeModal(true);
                }
              }}
            >
              {isDesktopViewport && 'Add'}
            </Button>
          </CommonTooltip>
        </Portal>
      </Portal>
      <Portal portalId="eqc-import-btn">
        <CommonTooltip title={addButtonTooltip}>
          <Button
            id="stage-import-btn"
            shape="round"
            icon={<Import />}
            className="import-button d-flex b-0"
            disabled={isStageItemSelectionActive || isAnyRowSelected}
            onClick={handleImport}
          >
            {isDesktopViewport && 'Import'}
          </Button>
        </CommonTooltip>
      </Portal>
      <div id="stage-table-wrapper" className="pointer">
        <DndContext
          id="stage-dnd-context"
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={DraggableBodyRow}
          sensors={sensors}
        >
          <SortableContext
            id="stage-sortable-context"
            items={dataSource.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <CommonTable
              id="stage-common-table"
              components={{ body: { row: Rows } }}
              columns={columns}
              dataSource={dataSource || []}
              onChange={handleTableChange}
              paginationConfig={paginationProp}
              rowKey="id"
              loading={loading || reorderLoading}
              rowSelection={getRowSelection()}
              rowClassName={(record) => {
                const classes = [];
                if (record?.id === stageId) classes.push('row-dark');
                if (isAnyRowSelected) classes.push('cursor-not-allowed');
                return classes.join(' ');
              }}
              onRow={(record) => {
                return {
                  onClick: (event) => {
                    if (event?.target?.closest('.ant-checkbox-wrapper')) {
                      return;
                    }
                    // Prevent row click navigation when stage items are selected
                    if (isStageItemSelectionActive) {
                      return;
                    }
                    if (record?.id) {
                      setStageId(record?.id);
                      setStageData?.(record);
                      setModalVisible(true);
                      setModalTitle(record?.name);
                    }
                  },
                };
              }}
            />
          </SortableContext>
        </DndContext>
      </div>
      {showUpgradeModal && (
        <UpgradeModal
          showModal={showUpgradeModal}
          setShowModal={setShowUpgradeModal}
          featureKey={REQUEST_FEATURE_UPGRADE_KEYS.STAGE_MAX_LIMIT}
          isAlreadyRequested={
            currentTenant?.tenant?.featureApprovalRequestConfig
              ?.stageMaxLimitRequestSent
          }
        />
      )}
    </div>
  );
};

export default StageListTable;
